<script module lang="ts">
  import { enemies, bosses, special } from '../../models/enemy.type';

  /**
   * The reverse of the link an enemy entry offers: enemies name the
   * environments they turn up in, so the roster for an environment has to be
   * built by inverting that. Done once for the section rather than once per row.
   */
  const enemiesByEnvironment: Record<string, Array<string>> = {};
  [...enemies, ...special, ...bosses].forEach((enemy) => {
    (enemy.environments ?? []).forEach((id) => {
      const key = id as string;
      enemiesByEnvironment[key] = enemiesByEnvironment[key] ?? [];
      enemiesByEnvironment[key].push(enemy.title);
    });
  });

  Object.values(enemiesByEnvironment).forEach((list) => list.sort());
</script>

<script lang="ts">
  import Portrait from '../../components/portrait.svelte';
  import { glossary } from '../../../store';
  import Text from '../../components/text.svelte';
  import type { EnvironmentDefinition } from '../../models/environment.type';

  interface Props {
    data: EnvironmentDefinition;
  }

  let { data }: Props = $props();

  let roster = $derived(enemiesByEnvironment[data.id as string] ?? []);

  /** Mountain fields 17, so the longer rosters are folded like the Faces list. */
  const CHIP_LIMIT = 10;
  let expanded = $state(false);
  let visibleRoster = $derived(expanded ? roster : roster.slice(0, CHIP_LIMIT));

  function openEnemy(title: string) {
    glossary.selected.set('enemies');
    glossary.searchTerm.set(title);
  }
</script>

<div class="container entry" style="--environment-colour: {data.colour}">
  <!-- Portrait resolves the scene art from the id via portraits.less. -->
  <div class="portrait" style="--background-colour: {data.colour}">
    <Portrait data={{ type: data.id }}></Portrait>
  </div>

  <div class="information">
    <div class="heading">
      <h2>{data.title}</h2>

      <div class="palette" title="Environment palette">
        <span class="swatch" style="background: {data.colourLight}"></span>
        <span class="swatch" style="background: {data.colour}"></span>
        <span class="swatch" style="background: {data.colourDark}"></span>
      </div>
    </div>

    {#if data.text}
      <Text data={data.text} class="blurb" />
    {/if}

    {#if roster.length}
      <div class="roster">
        <span class="roster-label">
          <b>{roster.length}</b>
          {roster.length === 1 ? 'enemy' : 'enemies'} here
        </span>

        <div class="chips">
          {#each visibleRoster as title (title)}
            <button class="chip" onclick={() => openEnemy(title)}>{title}</button>
          {/each}

          {#if roster.length > CHIP_LIMIT}
            <button class="more" onclick={() => (expanded = !expanded)}>
              {expanded ? 'Show fewer' : `+${roster.length - CHIP_LIMIT} more`}
            </button>
          {/if}
        </div>
      </div>
    {/if}
  </div>
</div>

<style lang="less">
  @import '../../../defines.less';

  .container {
    display: grid;
    grid-template-columns: auto minmax(0, 1fr);
    grid-gap: 16px;
    align-items: start;

    text-align: left;

    // No card. The environment's colour goes on the frame around its scene --
    // see --portrait-accent in components/portrait.svelte -- and the entry is
    // spaced and ruled off by .entry in glossary.svelte.
    --portrait-accent: var(--environment-colour);

    @media @mobile {
      grid-template-columns: minmax(0, 1fr);
      justify-items: center;
      text-align: center;
    }
  }

  .portrait {
    // Portrait sizes itself off --column-size, which is 300px app-wide for the
    // battle screen.
    --column-size: 170px;

    @media @mobile {
      --column-size: 140px;
    }
  }

  .information {
    display: flex;
    flex-direction: column;
    grid-gap: 8px;
    width: 100%;
    min-width: 0;
  }

  .heading {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    // Left-packed, not space-between: without a card edge to push against, a
    // right-aligned chip just floats off on its own halfway across the page.
    justify-content: flex-start;
    grid-gap: 10px;

    h2 {
      font-size: 1.4rem;
      text-transform: capitalize;
    }
  }

  .palette {
    display: flex;
    grid-gap: 3px;

    .swatch {
      width: 14px;
      height: 14px;
      border-radius: 3px;
      border: 1px solid rgba(0, 0, 0, 0.5);
    }
  }

  .container :global(.blurb) {
    font-size: 0.9rem;
    opacity: 0.85;
  }

  .roster {
    display: flex;
    flex-direction: column;
    grid-gap: 4px;
    width: 100%;

    @media @mobile {
      align-items: center;
    }
  }

  .roster-label {
    font-size: 0.65rem;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    opacity: 0.55;

    b {
      font-size: 0.8rem;
      opacity: 1;
    }
  }

  .chips {
    display: flex;
    flex-wrap: wrap;
    grid-gap: 4px;

    @media @mobile {
      justify-content: center;
    }

    .chip {
      // Reset of the global button.less pill.
      padding: 2px 10px;
      font-size: 0.7rem;
      background: var(--background-colour-dark);
      border: 1px solid var(--background-colour-light);
      border-radius: 999px;
      box-shadow: none;
      opacity: 0.75;
      transition:
        opacity 0.2s,
        border-color 0.2s,
        color 0.2s;

      &:hover,
      &:focus-visible {
        filter: none;
        opacity: 1;
        color: @edge-shine;
        border-color: @edge-warm;
      }

      &:active {
        box-shadow: none;
      }
    }

    .more {
      padding: 2px 10px;
      font-size: 0.7rem;
      font-style: italic;
      background: none;
      border: 1px dashed var(--background-colour-light);
      border-radius: 999px;
      box-shadow: none;
      opacity: 0.6;

      &:hover,
      &:focus-visible {
        filter: none;
        opacity: 1;
      }

      &:active {
        box-shadow: none;
      }
    }
  }
</style>
